import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import HubItemCard from './HubItemCard';
import NavigationDots from '../../../components/NavigationDots';
const FireIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M13.5.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67zM11.71 19c-1.78 0-3.22-1.4-3.22-3.14 0-1.62 1.05-2.76 2.81-3.12 1.77-.36 3.6-1.21 4.62-2.58.39 1.29.59 2.65.59 4.04 0 2.65-2.15 4.8-4.8 4.8z" />
  </svg>
);

const HotAgents = ({ agents = [], isLoading = false }) => {
  const [currentIndex, setCurrentIndex] = useState(1);
  const [isPaused, setIsPaused] = useState(false);

  // Ensure we have at least 3 agents for proper display
  const displayAgents = agents.slice(0, Math.min(agents.length, 10));
  const hasEnoughAgents = displayAgents.length >= 3;

  useEffect(() => {
    if (isPaused || !hasEnoughAgents) return;

    const interval = setInterval(() => {
      handleNext();
    }, 4000);

    return () => clearInterval(interval);
  }, [currentIndex, isPaused, hasEnoughAgents]);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % displayAgents.length);
  };

  const handlePrev = () => {
    setCurrentIndex(
      (prev) => (prev - 1 + displayAgents.length) % displayAgents.length
    );
  };

  const handleCardClick = (index) => {
    setCurrentIndex(index);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 5000);
  };

  const getVisibleAgents = () => {
    if (!hasEnoughAgents) return displayAgents;

    const prev =
      (currentIndex - 1 + displayAgents.length) % displayAgents.length;
    const next = (currentIndex + 1) % displayAgents.length;

    return [
      { agent: displayAgents[prev], position: 'left', index: prev },
      {
        agent: displayAgents[currentIndex],
        position: 'center',
        index: currentIndex,
      },
      { agent: displayAgents[next], position: 'right', index: next },
    ];
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-success" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!displayAgents || displayAgents.length === 0) {
    return (
      <div className="text-center py-5 text-muted">
        <FireIcon className="text-danger mb-3" size={32} />
        <p>No hot agents available</p>
      </div>
    );
  }

  if (!hasEnoughAgents) {
    return (
      <div className="hot-agents-container">
        <div className="d-flex align-items-center justify-content-center mb-4">
          <FireIcon className="text-danger ms-2" size={24} />
          <h4 className="mb-0 fw-bold">Hot Agents</h4>
          <FireIcon className="text-danger ms-2" size={24} />
        </div>
        <div className="row g-3">
          {displayAgents.map((agent, index) => (
            <div key={agent.id} className="col-12 col-md-6">
              <HubItemCard item={agent} type="agent" index={index} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const visibleAgents = getVisibleAgents();

  return (
    <div
      className="hot-agents-container py-4"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      style={{ minHeight: '600px' }}
    >
      {/* Header */}
      <div className="d-flex align-items-center justify-content-center mb-5">
        <motion.div
          animate={{ rotate: [0, 15, -15, 0] }}
          transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
          className="text-danger me-2"
        >
          <FireIcon size={24} />
        </motion.div>
        <h4 className="mb-0 fw-bold">Hot Agents</h4>
        <motion.div
          animate={{ rotate: [0, -15, 15, 0] }}
          transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
          className="text-danger ms-2"
        >
          <FireIcon size={24} />
        </motion.div>
      </div>

      {/* Carousel Container */}
      <div className="position-relative">
        <div className="d-flex align-items-start justify-content-center gap-3 position-relative mb-5">
          {/* Navigation Buttons */}
          <button
            className="btn btn-outline-secondary rounded-circle p-2 position-absolute d-none d-md-flex align-items-center justify-content-center"
            style={{
              left: '0',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              width: '40px',
              height: '40px',
            }}
            onClick={() => {
              handlePrev();
              setIsPaused(true);
              setTimeout(() => setIsPaused(false), 5000);
            }}
            aria-label="Previous agent"
          >
            <BsChevronLeft />
          </button>

          {/* Agent Cards */}
          <div
            className="d-flex align-items-start justify-content-center gap-3"
            style={{ width: '100%', maxWidth: '900px', paddingTop: '30px' }}
          >
            <AnimatePresence mode="popLayout">
              {visibleAgents.map(({ agent, position, index }) => (
                <motion.div
                  key={`${agent.id}-${position}`}
                  layout
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{
                    opacity: position === 'center' ? 1 : 0.6,
                    scale: position === 'center' ? 1.05 : 0.85,
                    y: position === 'center' ? -10 : 0,
                    zIndex: position === 'center' ? 2 : 1,
                    filter: position === 'center' ? 'none' : 'blur(1px)',
                  }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{
                    type: 'spring',
                    stiffness: 300,
                    damping: 25,
                  }}
                  className={`position-relative ${position !== 'center' ? 'd-none d-md-block' : ''}`}
                  style={{
                    width: position === 'center' ? '320px' : '280px',
                    maxWidth:
                      position === 'center' ? 'calc(100vw - 32px)' : '280px',
                    cursor: position !== 'center' ? 'pointer' : 'default',
                    flexShrink: 0,
                  }}
                  onClick={() =>
                    position !== 'center' && handleCardClick(index)
                  }
                >
                  {/* Glow effect for center card */}
                  {position === 'center' && (
                    <motion.div
                      className="position-absolute d-none d-md-block"
                      style={{
                        inset: '-15px',
                        background:
                          'radial-gradient(circle, rgba(255, 193, 7, 0.15) 0%, transparent 70%)',
                        borderRadius: '20px',
                        pointerEvents: 'none',
                      }}
                      animate={{
                        opacity: [0.5, 1, 0.5],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                    />
                  )}

                  <div
                    style={{
                      pointerEvents: position === 'center' ? 'auto' : 'none',
                    }}
                  >
                    <HubItemCard item={agent} type="agent" index={0} />
                  </div>

                  {/* Center badge */}
                  {position === 'center' && (
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      className="position-absolute"
                      style={{
                        top: '-15px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 3,
                      }}
                    >
                      <div className="badge bg-danger d-flex align-items-center gap-1 px-2 py-1">
                        <FireIcon />
                        <span>TRENDING</span>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          <button
            className="btn btn-outline-secondary rounded-circle p-2 position-absolute d-none d-md-flex align-items-center justify-content-center"
            style={{
              right: '0',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              width: '40px',
              height: '40px',
            }}
            onClick={() => {
              handleNext();
              setIsPaused(true);
              setTimeout(() => setIsPaused(false), 5000);
            }}
            aria-label="Next agent"
          >
            <BsChevronRight />
          </button>
        </div>

        {/* Progress Indicator */}
        <div className="d-flex justify-content-center align-items-center gap-2 mt-4">
          <NavigationDots
            activeIndex={currentIndex}
            totalItems={displayAgents.length}
          />
        </div>

        {/* Counter */}
        <div className="text-center mt-3">
          <small className="text-muted">
            Showing {currentIndex + 1} of {displayAgents.length} hot agents
          </small>
        </div>
      </div>
    </div>
  );
};

export default HotAgents;
